import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Login as LoginIcon,
  Logout as LogoutIcon,
  PeopleAlt as PeopleAltIcon,
  HelpOutline as HelpOutlineIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { updateNotificationSettings } from '../utils/api';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useGlobalContext } from '../contexts/GlobalContext';
import { DEFAULT_NOTIFICATION_SETTINGS } from '../hooks/useNotificationSetting';

const CATEGORY_OPTIONS = [
  {
    key: 'show_check_in',
    title: 'Check-In',
    description: 'Receive alerts for new person check-ins.',
    icon: LoginIcon,
    iconBg: '#dcfce7',
    iconColor: '#16a34a',
  },
  {
    key: 'show_check_out',
    title: 'Check-Out',
    description: 'Receive alerts for person check-outs.',
    icon: LogoutIcon,
    iconBg: '#fee2e2',
    iconColor: '#991b1b',
  },
  {
    key: 'show_possible_match',
    title: 'Possible Match',
    description: 'Receive alerts for potential matches.',
    icon: WarningIcon,
    iconBg: '#ffedd5',
    iconColor: '#f97316',
  },
  {
    key: 'show_unmatched',
    title: 'Unknown / Unmatched',
    description: 'Receive alerts for unidentified faces.',
    icon: ErrorIcon,
    iconBg: '#d32f2f',
    iconColor: '#ffffff',
  },
];

const durationModeFromSeconds = (seconds) => {
  if (seconds === null || seconds === undefined || seconds === '') return 'default';
  if ([3, 5, 10].includes(Number(seconds))) return String(Number(seconds));
  return 'custom';
};

const customSecondsFromSeconds = (seconds) =>
  seconds && ![3, 5, 10].includes(Number(seconds)) ? String(seconds) : '';

const CATEGORY_KEYS = CATEGORY_OPTIONS.map((item) => item.key);

const editableFromSettings = (source) => {
  const resolved = { ...DEFAULT_NOTIFICATION_SETTINGS, ...(source || {}) };
  const editable = { notifications_enabled: Boolean(resolved.notifications_enabled) };
  CATEGORY_KEYS.forEach((key) => {
    editable[key] = Boolean(resolved[key]);
  });
  return editable;
};

const NotificationSettings = () => {
  const { confirm } = useConfirmDialog();
  const {
    notificationSettings: settings,
    setNotificationSettings: setSettings,
    loadNotificationSettings,
    notificationSettingsLoaded
  } = useGlobalContext();
  const [draft, setDraft] = useState(() => editableFromSettings(DEFAULT_NOTIFICATION_SETTINGS));
  const [durationMode, setDurationMode] = useState('default');
  const [customSeconds, setCustomSeconds] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const dirtyRef = useRef(false);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  useEffect(() => {
    let cancelled = false;
    const applySource = (source) => {
      setDraft(editableFromSettings(source));
      setDurationMode(durationModeFromSeconds(source?.toast_duration_seconds));
      setCustomSeconds(customSecondsFromSeconds(source?.toast_duration_seconds));
      dirtyRef.current = false;
    };

    const loadSettings = async () => {
      if (notificationSettingsLoaded) {
        // Keep unsaved edits when the shared settings state refreshes elsewhere.
        if (!dirtyRef.current) applySource(settings);
        setLoading(false);
        return;
      };
      setLoading(true);
      setError('');
      try {
        const next = await loadNotificationSettings();
        if (cancelled) return;
        applySource(next);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load notification settings.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSettings();
    return () => {
      cancelled = true;
    };
  }, [loadNotificationSettings, notificationSettingsLoaded, settings]);

  const allCategoriesSelected = useMemo(
    () => CATEGORY_KEYS.every((key) => Boolean(draft[key])),
    [draft]
  );

  const someCategoriesSelected = useMemo(
    () => CATEGORY_KEYS.some((key) => Boolean(draft[key])),
    [draft]
  );

  const notificationsEnabled = Boolean(draft.notifications_enabled);

  const markDirty = () => {
    dirtyRef.current = true;
    setSuccess('');
  };

  const updateCategory = (key, checked) => {
    setDraft((prev) => ({ ...prev, [key]: checked }));
    markDirty();
  };

  const handleSelectAll = (checked) => {
    setDraft((prev) => {
      const next = { ...prev };
      CATEGORY_OPTIONS.forEach((item) => {
        next[item.key] = checked;
      });
      return next;
    });
    markDirty();
  };

  const buildPayload = () => {
    let toastDuration = null;
    if (durationMode === 'custom') {
      toastDuration = Number(customSeconds);
    } else if (durationMode !== 'default') {
      toastDuration = Number(durationMode);
    }

    return {
      notifications_enabled: Boolean(draft.notifications_enabled),
      show_check_in: Boolean(draft.show_check_in),
      show_check_out: Boolean(draft.show_check_out),
      show_possible_match: Boolean(draft.show_possible_match),
      show_unmatched: Boolean(draft.show_unmatched),
      toast_duration_seconds: toastDuration,
    };
  };

  const revertDraft = () => {
    setDraft(editableFromSettings(settings));
    setDurationMode(durationModeFromSeconds(settings.toast_duration_seconds));
    setCustomSeconds(customSecondsFromSeconds(settings.toast_duration_seconds));
    dirtyRef.current = false;
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    if (notificationsEnabled && durationMode === 'custom') {
      const value = Number(customSeconds);
      if (!Number.isFinite(value) || value < 1 || value > 60) {
        setError('Custom toast duration must be between 1 and 60 seconds.');
        return;
      }
    }

    const confirmed = await confirm({
      title: 'Save Notification Settings',
      message: 'Do you want to apply these notification preferences for your user account?',
      confirmLabel: 'Save & Confirm',
      cancelLabel: 'Cancel',
    });
    if (!confirmed) {
      revertDraft();
      return;
    }

    setSaving(true);
    try {
      const data = await updateNotificationSettings(buildPayload());
      const next = { ...DEFAULT_NOTIFICATION_SETTINGS, ...(data?.settings || {}) };
      setSettings(next);
      setDraft(editableFromSettings(next));
      setDurationMode(durationModeFromSeconds(next.toast_duration_seconds));
      setCustomSeconds(customSecondsFromSeconds(next.toast_duration_seconds));
      dirtyRef.current = false;
      setSuccess('Notification settings saved successfully.');
    } catch (err) {
      setError(err.message || 'Failed to save notification settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#111827' }}>
        Notification Settings
      </Typography>
      <Typography sx={{ color: '#6b7280', mb: 3 }}>
        Manage all system notification preferences.
      </Typography>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

      <Card sx={{ borderRadius: 2, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.10)' }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            Notification Preferences Configuration
          </Typography>

          {loading ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Paper
                variant="outlined"
                sx={{
                  borderColor: '#40518A',
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.7,
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#111827',
                    background: 'linear-gradient(90deg, #eef2ff 0%, #f8fafc 100%)',
                    borderBottom: '1px solid #c7d2fe',
                  }}
                >
                  Enable / Disable
                </Box>
                <Box sx={{ p: 1.5 }}>
                  {/* <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 0.5 }}>
                    Master Control
                  </Typography> */}
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Switch
                      checked={Boolean(draft.notifications_enabled)}
                      onChange={(event) => {
                        setDraft((prev) => ({ ...prev, notifications_enabled: event.target.checked }));
                        markDirty();
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#40518A' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#40518A' },
                      }}
                    />
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>All Notifications</Typography>
                      <Typography variant="caption" sx={{ color: '#374151' }}>
                        Suppresses all live toasts and bell icon notifications when disabled.
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Paper>

              <Grid
                container
                spacing={{ xs: 3, md: 6 }}
                sx={{
                  opacity: notificationsEnabled ? 1 : 0.5,
                  pointerEvents: notificationsEnabled ? 'auto' : 'none',
                }}
              >
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Notification Categories
                  </Typography>
                  <FormControlLabel
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                    control={
                      <Checkbox
                        size="small"
                        disabled={!notificationsEnabled}
                        checked={allCategoriesSelected}
                        indeterminate={someCategoriesSelected && !allCategoriesSelected}
                        onChange={(event) => handleSelectAll(event.target.checked)}
                        sx={{ color: '#40518A', '&.Mui-checked': { color: '#40518A' } }}
                      />
                    }
                    label="Select All"
                  />

                  <Stack spacing={1.1} sx={{ mt: 0.5 }}>
                    {CATEGORY_OPTIONS.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Stack key={item.key} direction="row" alignItems="flex-start" spacing={1.2}>
                          <Checkbox
                            size="small"
                            disabled={!notificationsEnabled}
                            checked={Boolean(draft[item.key])}
                            onChange={(event) => updateCategory(item.key, event.target.checked)}
                            sx={{ p: '7px',color: '#40518A', '&.Mui-checked': { color: '#40518A' } }}
                          />
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 50,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: item.iconBg,
                              color: item.iconColor,
                            }}
                          >
                            <Icon sx={{ fontSize: 16 }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: 14 }}>{item.title}</Typography>
                            <Typography variant="caption" sx={{ color: '#374151', fontSize: 11 }}>
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ borderLeft: { md: '1px solid #e5e7eb' }, pl: { md: 3 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                      Live Toast Visibility
                    </Typography>
                    <Typography sx={{ fontWeight: 500, mb: 1 }}>Duration</Typography>
                    <RadioGroup
                      value={durationMode}
                      disabled={!notificationsEnabled}
                      onChange={(event) => {
                        setDurationMode(event.target.value);
                        markDirty();
                      }}
                    >
                      <FormControlLabel value="default" sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }} control={<Radio size="small" sx={{ color: '#40518A', '&.Mui-checked': { color: '#40518A' } }} />} label="Default" />
                      <FormControlLabel value="3" sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }} control={<Radio size="small" sx={{ color: '#40518A', '&.Mui-checked': { color: '#40518A' } }} />} label="3 seconds" />
                      <FormControlLabel value="5" sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }} control={<Radio size="small" sx={{ color: '#40518A', '&.Mui-checked': { color: '#40518A' } }} />} label="5 seconds" />
                      <FormControlLabel value="10" sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }} control={<Radio size="small" sx={{ color: '#40518A', '&.Mui-checked': { color: '#40518A' } }} />} label="10 seconds" />
                      <Stack direction="row" alignItems="center" spacing={0}>
                        <FormControlLabel
                          value="custom"
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                          control={<Radio size="small" sx={{ color: '#40518A', '&.Mui-checked': { color: '#40518A' } }} />}
                          label="Custom"
                        />
                        <TextField
                          size="small"
                          type="number"
                          value={customSeconds}
                          disabled={!notificationsEnabled || durationMode !== 'custom'}
                          onChange={(event) => {
                            setCustomSeconds(event.target.value);
                            markDirty();
                          }}
                          inputProps={{ min: 1, max: 60 }}
                          sx={{ width: 150 }}
                        />
                      </Stack>
                    </RadioGroup>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  disabled={saving}
                  onClick={handleSave}
                  sx={{
                    bgcolor: '#40518A',
                    boxShadow: '0 5px 14px rgba(64, 81, 138, 0.35)',
                    '&:hover': { bgcolor: '#33416f' },
                  }}
                >
                  Save & Confirm
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default NotificationSettings;
